import * as React from "react";

const LoanNotes = ({ propertyId, loanNotes, currentUser, viewMode, onAddNote, onDeleteNote, onEditNote }) => {
    const [newNoteText, setNewNoteText] = React.useState('');
    const [isAdding, setIsAdding] = React.useState(false);
    const [editingNoteId, setEditingNoteId] = React.useState(null);
    const [editNoteText, setEditNoteText] = React.useState('');

    const handleAddNote = () => {
        if (newNoteText.trim()) {
            const newNote = {
                PROPERTY_ID: propertyId,
                NOTE_TEXT: newNoteText.trim(),
                DATE_CREATED: new Date().toLocaleDateString('en-US'),
                CREATED_BY: currentUser,
                LAST_MODIFIED_DATE: new Date().toLocaleDateString('en-US'),
                LAST_MODIFIED_BY: currentUser,
            };
            onAddNote(newNote);
            setNewNoteText('');
            setIsAdding(false);
        }
    };

    const handleCancel = () => {
        setNewNoteText('');
        setIsAdding(false);
    };

    const handleStartEdit = (note, index, e) => {
        e.preventDefault();
        e.stopPropagation();
        const noteIdentifier = note.ARCIMEDES_ROW_ID || `temp-${index}`;
        setEditingNoteId(noteIdentifier);
        setEditNoteText(note.NOTE_TEXT);
    };

    const handleSaveEdit = (note, index, e) => {
        e.preventDefault();
        e.stopPropagation();
        if (editNoteText.trim() && editNoteText.trim() !== note.NOTE_TEXT) {
            const updatedNote = {
                ...note,
                NOTE_TEXT: editNoteText.trim(),
                LAST_MODIFIED_DATE: new Date().toLocaleDateString('en-US'),
                LAST_MODIFIED_BY: currentUser,
            };
            if (onEditNote && typeof onEditNote === 'function') {
                onEditNote(updatedNote, index);
            }
        }
        setEditingNoteId(null);
        setEditNoteText('');
    };

    const handleCancelEdit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setEditingNoteId(null);
        setEditNoteText('');
    };

    const sortedNotes = loanNotes && loanNotes.length > 0
        ? [...loanNotes]
            .filter(note => note.DELETED !== '1' && note.DELETED !== 1)
            .sort((a, b) => new Date(b.DATE_CREATED) - new Date(a.DATE_CREATED))
        : [];

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        } catch (e) {
            return dateStr;
        }
    };

    return (
        <div className="Loan-notes-wrapper">
            {!viewMode && (
                <div className="Loan-notes-add-section">
                    {!isAdding ? (
                        <button 
                            className="Loan-notes-add-btn" 
                            onClick={() => setIsAdding(true)}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                            Add Loan Note
                        </button>
                    ) : (
                        <div className="Loan-notes-add-form">
                            <div className="Loan-notes-form-header">
                                <h4 className="Loan-notes-form-title">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                    </svg>
                                    New Loan Note
                                </h4>
                            </div>
                            <textarea
                                className="Loan-notes-textarea"
                                placeholder="Enter your loan note here..."
                                value={newNoteText}
                                onChange={(e) => setNewNoteText(e.target.value)}
                                rows={4}
                                autoFocus
                            />
                            <div className="Loan-notes-form-actions">
                                <button 
                                    className="Loan-notes-btn Loan-notes-btn-save" 
                                    onClick={handleAddNote}
                                    disabled={!newNoteText.trim()}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    Save Note
                                </button>
                                <button 
                                    className="Loan-notes-btn Loan-notes-btn-cancel" 
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="Loan-notes-list">
                {sortedNotes.length > 0 ? (
                    sortedNotes.map((note, index) => {
                        const noteIdentifier = note.ARCIMEDES_ROW_ID || `temp-${index}`;
                        const isEditing = editingNoteId === noteIdentifier;
                        
                        return (
                            <div key={note.ARCIMEDES_ROW_ID || index} className="Loan-note-card">
                                <div className="Loan-note-header">
                                    <div className="Loan-note-meta">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <polyline points="12 6 12 12 16 14"></polyline>
                                        </svg>
                                        <span className="Loan-note-date">{formatDate(note.DATE_CREATED)}</span>
                                        <span className="Loan-note-divider">•</span>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="12" cy="7" r="4"></circle>
                                        </svg>
                                        <span className="Loan-note-author">{note.CREATED_BY}</span>
                                    </div>
                                    {!viewMode && (
                                        <div className="Loan-note-actions">
                                            {!isEditing ? (
                                                <React.Fragment>
                                                    <button 
                                                        type="button"
                                                        className="Loan-note-edit"
                                                        onClick={(e) => handleStartEdit(note, index, e)}
                                                        title="Edit note"
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                        </svg>
                                                    </button>
                                                    {onDeleteNote && (
                                                        <button 
                                                            type="button"
                                                            className="Loan-note-delete"
                                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDeleteNote(note.ARCIMEDES_ROW_ID); }}
                                                            title="Delete note"
                                                        >
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <polyline points="3 6 5 6 21 6"></polyline>
                                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                            </svg>
                                                        </button>
                                                    )}
                                                </React.Fragment>
                                            ) : (
                                                <React.Fragment>
                                                    <button 
                                                        type="button"
                                                        className="Loan-note-save"
                                                        onClick={(e) => handleSaveEdit(note, index, e)}
                                                        title="Save changes"
                                                        disabled={!editNoteText.trim()}
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                            <polyline points="20 6 9 17 4 12"></polyline>
                                                        </svg>
                                                    </button>
                                                    <button 
                                                        type="button"
                                                        className="Loan-note-cancel"
                                                        onClick={(e) => handleCancelEdit(e)}
                                                        title="Cancel editing"
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <line x1="18" y1="6" x2="6" y2="18"></line>
                                                            <line x1="6" y1="6" x2="18" y2="18"></line>
                                                        </svg>
                                                    </button>
                                                </React.Fragment>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {!isEditing ? (
                                    <div className="Loan-note-content">
                                        {note.NOTE_TEXT}
                                    </div>
                                ) : (
                                    <textarea
                                        className="Loan-notes-textarea Loan-note-edit-textarea"
                                        value={editNoteText}
                                        onChange={(e) => setEditNoteText(e.target.value)}
                                        rows={4}
                                        autoFocus
                                    />
                                )}
                                {note.LAST_MODIFIED_BY && note.LAST_MODIFIED_DATE !== note.DATE_CREATED && !isEditing && (
                                    <div className="Loan-note-footer">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <polyline points="12 6 12 12 16 14"></polyline>
                                        </svg>
                                        Modified {formatDate(note.LAST_MODIFIED_DATE)} by {note.LAST_MODIFIED_BY}
                                    </div>
                                )}
                            </div>
                        );
                    })
                ) : (
                    <div className="Loan-notes-empty">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                            <line x1="12" y1="18" x2="12" y2="12"></line>
                            <line x1="9" y1="15" x2="15" y2="15"></line>
                        </svg>
                        {viewMode ? (
                            <React.Fragment>
                                <p className="Loan-notes-empty-title">No loan notes available</p>
                                <p className="Loan-notes-empty-subtitle">There are currently no notes for this loan</p>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <p className="Loan-notes-empty-title">No loan notes yet</p>
                                <p className="Loan-notes-empty-subtitle">Click "Add Loan Note" to create your first note</p>
                            </React.Fragment>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LoanNotes;
